import React, { PropsWithChildren, useMemo } from 'react';

import { DictionaryItemDto, DictionaryTypeDto, PermissionDto, UserShortDto } from '@/api/generated';
import { useGetAllDictionaries } from '@/api/requests/useGetAllDictionaries.util.ts';
import { useGetUsersShort } from '@/api/requests/useGetUsersShort.util.ts';
import { SelectSearchOptionModel } from '@/components/form/model/SelectSearchOption.model.ts';
import { FullPageSpinner } from '@/components/ui/fullPageSpinner/FullPageSpinner.comp.tsx';
import { useHasPermission } from '@/contexts/userContext/util/useHasPermission.util.ts';

import {
  DictionariesContext,
  DictionariesContextProps,
  DictionariesDefaultValues,
} from './Dictionaries.context';

const mapDict = (dict: DictionaryItemDto): SelectSearchOptionModel<number> => ({
  label: dict.name,
  value: dict.dictionaryId,
});

const mapUserShort = (user: UserShortDto): SelectSearchOptionModel => ({
  label: `${user.lastName} ${user.firstName}`,
  value: user.userId,
});

/**
 * Reference data of every module's forms: dictionary entries (read with dictionary read, which every
 * system role holds) and the users' short list (open to every signed-in user). A user without
 * dictionary read still gets the users, with empty dictionaries.
 */
export const DictionariesContextProvider: React.FC<PropsWithChildren> = props => {
  const canReadDictionaries = useHasPermission(PermissionDto.DictionaryRead);
  const { usersShort } = useGetUsersShort();

  const { dictionaries, isFetchingDictionariesTypes } = useGetAllDictionaries({
    enabled: canReadDictionaries,
  });

  const context: DictionariesContextProps = useMemo(() => {
    const groupedDictionaries: DictionariesContextProps = {
      ...DictionariesDefaultValues,
      ['USERS_SHORT']: usersShort.map(mapUserShort),
    };

    Object.entries(dictionaries ?? {}).forEach(([type, items]) => {
      groupedDictionaries[type as DictionaryTypeDto] = items.map(mapDict);
    });

    return groupedDictionaries;
  }, [dictionaries, usersShort]);

  if (isFetchingDictionariesTypes) {
    return <FullPageSpinner />;
  }

  return (
    <DictionariesContext.Provider value={context}>{props.children}</DictionariesContext.Provider>
  );
};
